import { fromBase64, toBase64 } from '../bytes/encoding';
import { ParseError } from '../bytes/errors';
import { importSpki, type PublicKey } from '../crypto/sig';
import { leafHash } from '../merkle/hash';
import type {
  ConsistencyProof,
  GetEntriesOptions,
  InclusionProof,
  InclusionTarget,
  LogClient,
  LogDescriptor,
  LogEntry,
  VerifiedTreeHead,
} from '../log/client';
import type { LogTransport } from '../transport/logTransport';
import { TransportError } from '../transport/errors';
import { parseExtraData, parseMerkleTreeLeaf } from './leaf';
import { parseSthJson, verifySth } from './sth';
import { sctFromJson } from './sct';
import type { Sct } from './types';

interface EntriesJson {
  entries: Array<{ leaf_input: string; extra_data: string }>;
}
interface ProofByHashJson {
  leaf_index: number;
  audit_path: string[];
}
interface EntryAndProofJson {
  leaf_input: string;
  extra_data: string;
  audit_path: string[];
}

/** Client for an RFC 6962 log (the classic `/ct/v1/` JSON API). */
export class Rfc6962Client implements LogClient {
  readonly kind = 'rfc6962' as const;
  readonly supportsHashLookup = true;
  /** Observed maximum get-entries batch size; starts optimistic and shrinks to what the log serves. */
  pageSize: number;
  private keyPromise?: Promise<PublicKey>;

  constructor(
    readonly log: LogDescriptor,
    private readonly transport: LogTransport,
    opts: { initialPageSize?: number; onPageSize?: (n: number) => void } = {},
  ) {
    this.pageSize = opts.initialPageSize ?? 256;
    this.onPageSize = opts.onPageSize;
  }
  private readonly onPageSize: ((n: number) => void) | undefined;

  private api(path: string, params?: Record<string, string | number>): string {
    const u = new URL('ct/v1/' + path, this.log.url);
    if (params) for (const [k, v] of Object.entries(params)) u.searchParams.set(k, String(v));
    return u.toString();
  }

  private key(): Promise<PublicKey> | undefined {
    if (!this.log.key) return undefined;
    this.keyPromise ??= importSpki(this.log.key);
    return this.keyPromise;
  }

  async getTreeHead(signal?: AbortSignal): Promise<VerifiedTreeHead> {
    const init = signal ? { signal } : undefined;
    const { value, text } = await this.transport.json(this.log, 'tree-head', this.api('get-sth'), init);
    const sth = parseSthJson(value);
    const key = this.key();
    let signature: VerifiedTreeHead['signature'] = { status: 'no-key' };
    if (key) {
      const r = await verifySth(sth, await key);
      signature = r.ok ? { status: 'verified' } : { status: 'failed', error: r.error ?? 'signature mismatch' };
    }
    return { treeSize: sth.treeSize, rootHash: sth.rootHash, timestamp: sth.timestamp, signature, raw: text, fetchedAt: Date.now() };
  }

  async getEntries(start: number, count: number, opts: GetEntriesOptions = {}): Promise<LogEntry[]> {
    if (count <= 0) return [];
    let end = start + count - 1;
    if (opts.treeSize !== undefined) end = Math.min(end, opts.treeSize - 1);
    if (end < start) return [];
    const init = opts.signal ? { signal: opts.signal } : undefined;
    const { value } = await this.transport.json<EntriesJson>(this.log, 'entries', this.api('get-entries', { start, end }), init);
    if (!Array.isArray(value?.entries)) throw new ParseError('get-entries: missing entries array');
    const requested = end - start + 1;
    if (value.entries.length < requested && value.entries.length > 0 && (opts.treeSize === undefined || end < opts.treeSize - 1 || value.entries.length < requested)) {
      // The log capped the batch (or we hit the tree end — harmless to shrink either way).
      if (opts.treeSize === undefined || end < opts.treeSize - 1) this.setPageSize(value.entries.length);
    }
    const out: LogEntry[] = [];
    for (let i = 0; i < value.entries.length; i++) {
      const e = value.entries[i]!;
      out.push(await decodeEntry(start + i, fromBase64(e.leaf_input), fromBase64(e.extra_data)));
    }
    return out;
  }

  private setPageSize(n: number): void {
    if (n > 0 && n !== this.pageSize) {
      this.pageSize = n;
      this.onPageSize?.(n);
    }
  }

  async getInclusionProof(target: InclusionTarget, treeSize: number, signal?: AbortSignal): Promise<InclusionProof> {
    const init = signal ? { signal } : undefined;
    if ('index' in target) {
      const { value } = await this.transport.json<EntryAndProofJson>(
        this.log,
        'proof',
        this.api('get-entry-and-proof', { leaf_index: target.index, tree_size: treeSize }),
        init,
      );
      if (!Array.isArray(value?.audit_path) || typeof value.leaf_input !== 'string') throw new ParseError('get-entry-and-proof: malformed response');
      const leaf = fromBase64(value.leaf_input);
      return { leafIndex: target.index, treeSize, auditPath: value.audit_path.map(fromBase64), leaf, leafHash: await leafHash(leaf) };
    }
    const { value } = await this.transport.json<ProofByHashJson>(
      this.log,
      'proof',
      this.api('get-proof-by-hash', { hash: toBase64(target.leafHash), tree_size: treeSize }),
      init,
    );
    if (!Array.isArray(value?.audit_path) || typeof value.leaf_index !== 'number') throw new ParseError('get-proof-by-hash: malformed response');
    return { leafIndex: value.leaf_index, treeSize, auditPath: value.audit_path.map(fromBase64), leafHash: target.leafHash };
  }

  async getConsistency(first: number, second: number, signal?: AbortSignal): Promise<ConsistencyProof> {
    const init = signal ? { signal } : undefined;
    const { value } = await this.transport.json<{ consistency: string[] }>(
      this.log,
      'proof',
      this.api('get-sth-consistency', { first, second }),
      init,
    );
    if (!Array.isArray(value?.consistency)) throw new ParseError('get-sth-consistency: malformed response');
    return { first, second, proof: value.consistency.map(fromBase64) };
  }

  async getRoots(signal?: AbortSignal): Promise<Uint8Array[]> {
    const init = signal ? { signal } : undefined;
    const { value } = await this.transport.json<{ certificates: string[] }>(this.log, 'roots', this.api('get-roots'), init);
    if (!Array.isArray(value?.certificates)) throw new ParseError('get-roots: malformed response');
    return value.certificates.map(fromBase64);
  }

  async addChain(chain: Uint8Array[], precert: boolean, signal?: AbortSignal): Promise<Sct> {
    const url = new URL('ct/v1/' + (precert ? 'add-pre-chain' : 'add-chain'), this.log.submissionUrl).toString();
    const { value } = await this.transport.json(this.log, 'submit', url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ chain: chain.map(toBase64) }),
      ...(signal ? { signal } : {}),
    });
    return sctFromJson(value);
  }
}

/** Shared by both clients: turns leaf_input/extra_data (or their tile equivalents) into a LogEntry. */
export async function decodeEntry(index: number, leafInput: Uint8Array, extraData?: Uint8Array): Promise<LogEntry> {
  const leaf = parseMerkleTreeLeaf(leafInput);
  const entry: LogEntry = {
    index,
    leaf,
    leafHash: await leafHash(leafInput),
    entryType: leaf.entry.entryType,
    cert: leaf.entry.entryType === 'x509' ? leaf.entry.x509! : leaf.entry.precert!.tbs,
    chain: [],
  };
  if (leaf.entry.precert) entry.issuerKeyHash = leaf.entry.precert.issuerKeyHash;
  if (extraData) {
    const extra = parseExtraData(extraData, leaf.entry.entryType);
    if (extra.precert) entry.precert = extra.precert;
    entry.chain = extra.chain.map((der) => ({ der }));
  }
  return entry;
}

export { TransportError };
